import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails, Chip, Stack, CircularProgress,
  Paper, IconButton, Tooltip, Snackbar, Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ControlsGrid from './ControlsGrid';
import { useDispatch, useSelector } from 'react-redux';
import { downloadComplianceReport, clearAllComplianceMessages } from '../../../store/compliance/complianceSlice';

const ControlsCategoriesView = ({
  controls = [],
  onResourceClick,
  onSectionSelect,
  selectedSection,
  sectionControls,
  isLoadingControls,
  controlsError,
  compliance,
  runId,
  accountNumber,
}) => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const dispatch = useDispatch();
  const {
    isDownloadingComplianceReport,
    downloadComplianceReportMessage,
    downloadComplianceReportError
  } = useSelector((state) => state.compliance);

  // Prepare categories from controls array
  const categories = useMemo(() => {
    if (!Array.isArray(controls)) return [];
    return controls.map((category) => ({
      name: category.Section,
      path: category.SectionPath,
      metrics: {
        total: category.TotalControls,
        passed: category.PassedControls,
        failed: category.FailedControls,
        manual: category.ManualControls,
      },
    }));
  }, [controls]);

  const handleCategoryChange = (categoryPath) => (event, isExpanded) => {
    setExpandedCategory(isExpanded ? categoryPath : null);
    if (isExpanded) {
      onSectionSelect(categoryPath);
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      {/* Header Bar */}
      <Box 
        sx={{ 
          p: 2,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
          Compliance Controls
        </Typography>
      </Box>

      {/* Accordions Container */}
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {categories.map((category) => (
          <Accordion
            key={category.path}
            expanded={expandedCategory === category.path}
            onChange={handleCategoryChange(category.path)}
            sx={{
              '&:before': { display: 'none' },
              boxShadow: 'none',
              border: '1px solid #e0e0e0',
              borderRadius: '8px !important',
              overflow: 'hidden',
              '&:hover': {
                boxShadow: '0px 2px 8px rgba(0,0,0,0.05)',
              },
            }}
          >
            <AccordionSummary
              sx={{
                backgroundColor: '#ffffff',
                borderBottom: '1px solid #e0e0e0',
                '&:hover': { backgroundColor: '#f8f9fa' },
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                  {expandedCategory === category.path ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', flex: 1 }}>
                  {category.name}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    minWidth: 340,
                    gap: 0,
                  }}
                >
                  <Box sx={{ minWidth: 140, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Typography variant="body2" sx={{ color: '#495057', fontWeight: 500, fontSize: 15, width: '100%', whiteSpace: 'nowrap' }}>
                      Total Controls:
                      <span style={{ fontFamily: "inherit", fontWeight: 500, marginLeft: 6, minWidth: 20, display: 'inline-block', textAlign: 'right' }}>{category.metrics.total}</span>
                    </Typography>
                  </Box>
                  <Box sx={{ width: 90, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500, fontSize: 15, width: '100%' }}>
                      Passed:
                      <span style={{ fontFamily: "inherit", fontWeight: 500, marginLeft: 6, minWidth: 20, display: 'inline-block', textAlign: 'right' }}>{category.metrics.passed}</span>
                    </Typography>
                  </Box>
                  <Box sx={{ width: 90, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 500, fontSize: 15, width: '100%' }}>
                      Failed:
                      <span style={{ fontFamily: "inherit", fontWeight: 500, marginLeft: 6, minWidth: 20, display: 'inline-block', textAlign: 'right' }}>{category.metrics.failed}</span>
                    </Typography>
                  </Box>
                  <Box sx={{ width: 90, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 500, fontSize: 15, width: '100%' }}>
                      Manual:
                      <span style={{ fontFamily: "inherit", fontWeight: 500, marginLeft: 6, minWidth: 20, display: 'inline-block', textAlign: 'right' }}>{category.metrics.manual}</span>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {expandedCategory === category.path ? (
                isLoadingControls ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : controlsError ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography color="error">Error loading controls. Please try again.</Typography>
                  </Box>
                ) : sectionControls && sectionControls.controlsListJson ? (
                  <ControlsGrid
                    controls={sectionControls.controlsListJson}
                    onResourceClick={onResourceClick}
                    compliance={compliance}
                    accountNumber={accountNumber}
                  />
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography color="text.secondary">No controls found for this section.</Typography>
                  </Box>
                )
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="text.secondary">Expand to view controls</Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Paper>
  );
};

export default ControlsCategoriesView; 